import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface DatasourceFolderState {
  expandedFolders: Set<string>
  version: number
}

interface DatasourceFolderStore {
  state: DatasourceFolderState

  // Check if folder is expanded
  isFolderExpanded: (folderId: string) => boolean

  // Toggle folder expansion
  toggleFolder: (folderId: string) => void

  // Toggle folder with all children
  toggleFolderWithChildren: (folderId: string, childFolderIds: string[]) => void

  // Explicitly set folder expansion state
  setFolderExpanded: (folderId: string, expanded: boolean) => void

  // Expand specific folder
  expandFolder: (folderId: string) => void

  // Collapse specific folder
  collapseFolder: (folderId: string) => void

  // Collapse all folders
  collapseAll: () => void

  // Expand all folders
  expandAll: (folderIds: string[]) => void

  // Expand multiple folders at once
  expandMultiple: (folderIds: string[]) => void

  // Cleanup deleted folders from state
  cleanupDeletedFolders: (existingFolderIds: string[]) => void

  // Clear all expansion state
  clearExpansionState: () => void

  // Check if any folders are expanded
  hasExpandedFolders: () => boolean
}

const CURRENT_VERSION = 1

const defaultState: DatasourceFolderState = {
  expandedFolders: new Set<string>(),
  version: CURRENT_VERSION,
}

export const useDatasourceFolderStore = create<DatasourceFolderStore>()(
  persist(
    (set, get) => ({
      state: defaultState,

      isFolderExpanded: (folderId: string) => {
        const { state } = get()
        return state.expandedFolders.has(folderId)
      },

      toggleFolder: (folderId: string) => {
        set((store) => {
          const newExpanded = new Set(store.state.expandedFolders)
          if (newExpanded.has(folderId)) {
            newExpanded.delete(folderId)
          } else {
            newExpanded.add(folderId)
          }
          return {
            state: {
              ...store.state,
              expandedFolders: newExpanded,
              version: CURRENT_VERSION,
            },
          }
        })
      },

      toggleFolderWithChildren: (folderId: string, childFolderIds: string[]) => {
        set((store) => {
          const newExpanded = new Set(store.state.expandedFolders)

          if (newExpanded.has(folderId)) {
            // Collapsing: remove the folder and all its children
            newExpanded.delete(folderId)
            for (const childId of childFolderIds) {
              newExpanded.delete(childId)
            }
          } else {
            // Expanding: just add the folder itself
            newExpanded.add(folderId)
          }

          return {
            state: {
              ...store.state,
              expandedFolders: newExpanded,
              version: CURRENT_VERSION,
            },
          }
        })
      },

      setFolderExpanded: (folderId: string, expanded: boolean) => {
        set((store) => {
          const newExpanded = new Set(store.state.expandedFolders)
          if (expanded) {
            newExpanded.add(folderId)
          } else {
            newExpanded.delete(folderId)
          }
          return {
            state: {
              ...store.state,
              expandedFolders: newExpanded,
              version: CURRENT_VERSION,
            },
          }
        })
      },

      expandFolder: (folderId: string) => {
        set((store) => {
          const newExpanded = new Set([...store.state.expandedFolders, folderId])
          return {
            state: {
              ...store.state,
              expandedFolders: newExpanded,
              version: CURRENT_VERSION,
            },
          }
        })
      },

      collapseFolder: (folderId: string) => {
        set((store) => {
          const newExpanded = new Set(store.state.expandedFolders)
          newExpanded.delete(folderId)
          return {
            state: {
              ...store.state,
              expandedFolders: newExpanded,
              version: CURRENT_VERSION,
            },
          }
        })
      },

      collapseAll: () => {
        set({
          state: {
            expandedFolders: new Set<string>(),
            version: CURRENT_VERSION,
          },
        })
      },

      expandAll: (folderIds: string[]) => {
        set({
          state: {
            expandedFolders: new Set<string>(folderIds),
            version: CURRENT_VERSION,
          },
        })
      },

      expandMultiple: (folderIds: string[]) => {
        set((store) => {
          const newExpanded = new Set(store.state.expandedFolders)
          for (const folderId of folderIds) {
            newExpanded.add(folderId)
          }
          return {
            state: {
              ...store.state,
              expandedFolders: newExpanded,
              version: CURRENT_VERSION,
            },
          }
        })
      },

      cleanupDeletedFolders: (existingFolderIds: string[]) => {
        set((store) => {
          const existingSet = new Set(existingFolderIds)
          const cleanedFolders = new Set(
            Array.from(store.state.expandedFolders).filter((id) => existingSet.has(id))
          )

          return {
            state: {
              expandedFolders: cleanedFolders,
              version: CURRENT_VERSION,
            },
          }
        })
      },

      clearExpansionState: () => {
        set({
          state: {
            expandedFolders: new Set<string>(),
            version: CURRENT_VERSION,
          },
        })
      },

      hasExpandedFolders: () => {
        const { state } = get()
        return state.expandedFolders.size > 0
      },
    }),
    {
      name: 'datasource-folder-state',
      // Only persist the 'state' field, not the methods
      partialize: (store) => ({ state: store.state }),
      storage: {
        getItem: (name) => {
          try {
            const str = localStorage.getItem(name)
            if (!str) return null

            const stored = JSON.parse(str) as { state?: { state?: { expandedFolders?: string[]; version?: number } } }
            if (!stored.state?.state) return null

            const savedState = stored.state.state
            const version = typeof savedState.version === 'number' ? savedState.version : 0

            if (version < CURRENT_VERSION) {
              // Migration needed - reset state
              return null
            }

            // Return in the format persist middleware expects
            // The persist middleware wraps our store in { state: <our store>, version: <persist version> }
            return {
              state: {
                state: {
                  expandedFolders: new Set<string>(
                    Array.isArray(savedState.expandedFolders) ? savedState.expandedFolders : []
                  ),
                  version: savedState.version || CURRENT_VERSION,
                },
              },
            }
          } catch (error) {
            console.error('Failed to load datasource folder state:', error)
            return null
          }
        },
        setItem: (name, value) => {
          try {
            // Value from persist: { state: { state: { expandedFolders: Set, version } }, version: 0 }
            const persistState = value as { state?: { state?: DatasourceFolderState } }
            const ourState = persistState.state?.state

            if (!ourState) {
              console.error('Failed to save: ourState is undefined')
              return
            }

            const toStore = {
              state: {
                state: {
                  expandedFolders: Array.from(ourState.expandedFolders),
                  version: ourState.version,
                },
              },
            }
            localStorage.setItem(name, JSON.stringify(toStore))
          } catch (error) {
            console.error('Failed to save datasource folder state:', error)
          }
        },
        removeItem: (name) => {
          try {
            localStorage.removeItem(name)
          } catch (error) {
            console.error('Failed to remove datasource folder state:', error)
          }
        },
      },
    }
  )
)
